import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import axiosInstance from '../utils/axiosInstance';
import { plotlyStyle, formatLabel } from '../utils/plotlyStyle';
import { Stack, Button, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { MESSAGES } from '../constants/messages';

const ChartClientOrder = () => {
  const [orderData, setOrderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const now = dayjs();

  const defaultFrom = now
    .subtract(11, 'month')
    .startOf('month');

  const defaultTo = now.endOf('month');

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const fetchOrderData = async (params = {}) => {
    setLoading(true);

    try {
      const res = await axiosInstance.get(
        '/chart/client-order-count',
        { params }
      );

      setOrderData(res.data);
      setError(null);
    } catch (err) {
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderData({
      from: defaultFrom.format('YYYY-MM-DD'),
      to: defaultTo.format('YYYY-MM-DD'),
    });
  }, []);

  const applyFilter = () => {
    fetchOrderData({
      from: from.startOf('month').format('YYYY-MM-DD'),
      to: to.endOf('month').format('YYYY-MM-DD'),
    });
  };

  const setThisMonth = () => {
    const f = dayjs().startOf('month');
    const t = dayjs().endOf('month');

    setFrom(f);
    setTo(t);

    fetchOrderData({
      from: f.format('YYYY-MM-DD'),
      to: t.format('YYYY-MM-DD'),
    });
  };

  const setLast3Months = () => {
    const f = dayjs()
      .subtract(2, 'month')
      .startOf('month');

    const t = dayjs().endOf('month');

    setFrom(f);
    setTo(t);

    fetchOrderData({
      from: f.format('YYYY-MM-DD'),
      to: t.format('YYYY-MM-DD'),
    });
  };

  const setLast12Months = () => {
    const f = dayjs()
      .subtract(11, 'month')
      .startOf('month');

    const t = dayjs().endOf('month');

    setFrom(f);
    setTo(t);

    fetchOrderData({
      from: f.format('YYYY-MM-DD'),
      to: t.format('YYYY-MM-DD'),
    });
  };

  if (loading) {
    return (
      <Typography>
        {MESSAGES.LOADING}
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography>
        {error || MESSAGES.LOAD_FAILED}
      </Typography>
    );
  }

  const orderPlotData = {
    x: orderData.map(item =>
      formatLabel(item.client_name)
    ),

    y: orderData.map(item =>
      item.order_count
    ),

    text: orderData.map(
      item => item.client_name
    ),

    type: 'bar',

    name: '顧客別注文数',

    marker: {
      color: plotlyStyle.colors.bar,
    },

    hovertemplate:
      '%{text}<br>' +
      '注文数: %{y} 件' +
      '<extra></extra>',
  };

  const orderLayout = {
    ...plotlyStyle.layout,

    title: {
      text:
        `顧客別注文数（${from.format('YYYY-MM')}〜${to.format('YYYY-MM')}）`,
    },

    xaxis: {
      ...plotlyStyle.layout.xaxis,
    },

    yaxis: {
      ...plotlyStyle.layout.yaxis,
      title: {
        text: '注文数',
      },
      tickformat: ',.0f',
    },
  };

  return (
    <>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale="ja"
      >
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <DatePicker
              label="開始日"
              views={['year', 'month']}
              value={from}
              onChange={setFrom}
              maxDate={to}
              format="YYYY年M月"
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />

            <Typography>〜</Typography>

            <DatePicker
              label="終了日"
              views={['year', 'month']}
              value={to}
              onChange={setTo}
              minDate={from}
              format="YYYY年M月"
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />

            <Button
              size="small"
              variant="contained"
              onClick={applyFilter}
            >
              適用
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={setThisMonth}
            >
              今月
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={setLast3Months}
            >
              直近3ヶ月
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={setLast12Months}
            >
              直近12ヶ月
            </Button>
          </Stack>
        </Stack>
      </LocalizationProvider>

      <div>
        <Plot
          data={[orderPlotData]}
          layout={orderLayout}
          style={{
            width: '100%',
            height: '100%',
            minHeight: 500,
          }}
          useResizeHandler
        />
      </div>
    </>
  );
};

export default ChartClientOrder;